import React, { useState, useEffect, useMemo } from 'react';
import { usePantryItems } from '../hooks/usePantryItems';
import { addPantryItem, updatePantryItem, deletePantryItem, incrementPantryQuantity } from '../lib/pantryService';
import { addPantryItemsToShoppingList } from '../lib/shoppingListService';
import PantryItemForm from '../components/PantryItemForm';
import PantryItemList from '../components/PantryItemList';
import ImageUploader from '../components/ImageUploader';
import AnalyzedItemsDialog from '../components/AnalyzedItemsDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { useToast } from '../components/Toast';

const PantryPage = () => {
  const { items, loading, error } = usePantryItems();
  const { showToast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [analyzedItems, setAnalyzedItems] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pantry', 'refrigerator'
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [zeroQuantityFilter, setZeroQuantityFilter] = useState(false); // Filter for items with 0 quantity
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'expiration', 'category'
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, itemId: null });

  const handleAddItem = async (itemData) => {
    try {
      await addPantryItem(itemData);
      setShowAddForm(false);
      showToast('Item added successfully!', 'success');
    } catch (error) {
      console.error('Error adding item:', error);
      showToast('Failed to add item: ' + error.message, 'error');
    }
  };

  const handleUpdateItem = async (itemId, itemData) => {
    try {
      await updatePantryItem(itemId, itemData);
      setEditingItem(null);
      showToast('Item updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating item:', error);
      showToast('Failed to update item: ' + error.message, 'error');
    }
  };

  const handleDeleteItem = (itemId) => {
    setDeleteConfirm({ isOpen: true, itemId });
  };

  const confirmDeleteItem = async () => {
    const itemId = deleteConfirm.itemId;
    setDeleteConfirm({ isOpen: false, itemId: null });
    try {
      await deletePantryItem(itemId);
      showToast('Item deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting item:', error);
      showToast('Failed to delete item: ' + error.message, 'error');
    }
  };

  const handleIncrementQuantity = async (itemId, amount) => {
    try {
      await incrementPantryQuantity(itemId, amount);
      // No toast for quantity updates to avoid spam
    } catch (error) {
      console.error('Error updating quantity:', error);
      showToast('Failed to update quantity: ' + error.message, 'error');
    }
  };

  const handleToggleSelect = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleAddSelectedToShoppingList = async () => {
    if (selectedItems.size === 0) {
      showToast('Please select at least one item', 'warning');
      return;
    }
    const selectedCount = selectedItems.size;
    try {
      await addPantryItemsToShoppingList(Array.from(selectedItems));
      setSelectedItems(new Set());
      showToast(`Added ${selectedCount} item(s) to shopping list!`, 'success');
    } catch (error) {
      console.error('Error adding to shopping list:', error);
      showToast('Failed to add items to shopping list: ' + error.message, 'error');
    }
  };

  const handleBulkRecategorize = async (newCategory) => {
    if (selectedItems.size === 0) {
      showToast('Please select at least one item', 'warning');
      return;
    }
    const selectedCount = selectedItems.size;
    try {
      const updates = Array.from(selectedItems).map(itemId => {
        return updatePantryItem(itemId, { category: newCategory });
      });
      await Promise.all(updates);
      setSelectedItems(new Set());
      setShowBulkCategoryModal(false);
      showToast(`Updated category for ${selectedCount} item(s)!`, 'success');
    } catch (error) {
      console.error('Error updating categories:', error);
      showToast('Failed to update categories: ' + error.message, 'error');
    }
  };

  // Get all unique categories from items
  const allCategories = useMemo(() => {
    const categories = new Set();
    items.forEach(item => {
      if (item.category && item.category.trim()) {
        categories.add(item.category.trim());
      }
    });
    return Array.from(categories).sort();
  }, [items]);

  // Client-side filtering, searching, and sorting
  const processedItems = useMemo(() => {
    let result = [...items];

    // Filter by location
    if (filter !== 'all') {
      result = result.filter(item => item.location === filter);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      result = result.filter(item => 
        item.category && item.category.toLowerCase() === categoryFilter.toLowerCase()
      );
    }

    // Filter by zero quantity
    if (zeroQuantityFilter) {
      result = result.filter(item => (item.quantity || 0) === 0);
    }

    // Search filter (name or category)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item => 
        item.name.toLowerCase().includes(query) ||
        (item.category && item.category.toLowerCase().includes(query))
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
        case 'price':
          return (a.price || 0) - (b.price || 0);
        case 'expiration': {
          // Items without expiration dates go to the end
          if (!a.expirationDate && !b.expirationDate) return 0;
          if (!a.expirationDate) return 1;
          if (!b.expirationDate) return -1;
          
          const dateA = a.expirationDate instanceof Date 
            ? a.expirationDate 
            : a.expirationDate.toDate?.() || new Date(a.expirationDate);
          const dateB = b.expirationDate instanceof Date 
            ? b.expirationDate 
            : b.expirationDate.toDate?.() || new Date(b.expirationDate);
          return dateA - dateB;
        }
        case 'category':
          const catA = (a.category || '').toLowerCase();
          const catB = (b.category || '').toLowerCase();
          if (catA === catB) {
            return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
          }
          return catA.localeCompare(catB);
        default:
          return 0;
      }
    });

    return result;
  }, [items, filter, categoryFilter, zeroQuantityFilter, searchQuery, sortBy]);

  const handleImageAnalysis = (result) => {
    setAnalyzedItems(result.items || result);
    setShowImageUpload(false);
  };

  const handleImageError = (error) => {
    showToast('Image analysis error: ' + error, 'error');
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && (showAddForm || editingItem)) {
        setShowAddForm(false);
        setEditingItem(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showAddForm, editingItem]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading pantry...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md m-4">
        Error loading pantry: {error.message}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-purple-700 mb-4">
            Pantry & Refrigerator
          </h1>

          {/* Search and Filters */}
          <div className="space-y-3 mb-4">
            {/* Search Box */}
            <div className="bg-white rounded-lg shadow-sm p-3">
              <input
                type="text"
                placeholder="Search by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap gap-3">
              {/* Location Filters */}
              <div className="flex space-x-2 overflow-x-auto pb-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    filter === 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  All Locations
                </button>
                <button
                  onClick={() => setFilter('pantry')}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    filter === 'pantry'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  Pantry
                </button>
                <button
                  onClick={() => setFilter('refrigerator')}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    filter === 'refrigerator'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  Refrigerator
                </button>
                <button
                  onClick={() => setZeroQuantityFilter(!zeroQuantityFilter)}
                  className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                    zeroQuantityFilter
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  ⚠️ Needs Replenishment
                </button>
              </div>

              {/* Category Filter */}
              <div className="flex-1 min-w-[150px]">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                >
                  <option value="all">All Categories</option>
                  {allCategories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div className="flex-1 min-w-[150px]">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                >
                  <option value="name">Sort by Name</option>
                  <option value="price">Sort by Price</option>
                  <option value="expiration">Sort by Expiration</option>
                  <option value="category">Sort by Category</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {selectedItems.size > 0 && (
                <>
                  <button
                    onClick={() => setShowBulkCategoryModal(true)}
                    className="bg-orange-600 text-white px-3 py-2 rounded-md hover:bg-orange-700 text-sm font-medium flex-1 sm:flex-none"
                  >
                    📝 Re-categorize ({selectedItems.size})
                  </button>
                  <button
                    onClick={handleAddSelectedToShoppingList}
                    className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm font-medium flex-1 sm:flex-none"
                  >
                    Add {selectedItems.size} to List
                  </button>
                </>
              )}
              <button
                onClick={() => setShowImageUpload(!showImageUpload)}
                className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
              >
                📷 Upload
              </button>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 text-sm font-medium"
              >
                + Add Item
              </button>
            </div>
          </div>
        </div>

        {/* Image Upload */}
        {showImageUpload && (
          <div className="mb-6">
            <ImageUploader
              onAnalyzeComplete={handleImageAnalysis}
              onError={handleImageError}
            />
          </div>
        )}

        {/* Add/Edit Modal */}
        {(showAddForm || editingItem) && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddForm(false);
                setEditingItem(null);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? 'Edit Item' : 'Add Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <PantryItemForm
                  item={editingItem}
                  defaultLocation={editingItem ? null : filter === 'refrigerator' ? 'refrigerator' : filter === 'pantry' ? 'pantry' : 'pantry'}
                  existingCategories={allCategories}
                  onSave={editingItem 
                    ? (data) => handleUpdateItem(editingItem.id, data)
                    : handleAddItem}
                  onCancel={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Pantry Items List */}
        <PantryItemList
          items={processedItems}
          selectedItems={selectedItems}
          onToggleSelect={handleToggleSelect}
          onEdit={setEditingItem}
          onDelete={handleDeleteItem}
          onIncrementQuantity={handleIncrementQuantity}
        />

        {/* Bulk Re-categorize Modal */}
        {showBulkCategoryModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowBulkCategoryModal(false);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Re-categorize {selectedItems.size} Item(s)
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Select a new category for the selected items
                </p>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    id="bulk-category-select"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    defaultValue=""
                  >
                    <option value="">-- Select Category --</option>
                    {allCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or create new category:
                  </label>
                  <input
                    type="text"
                    id="bulk-category-new"
                    placeholder="Enter new category name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowBulkCategoryModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const selectEl = document.getElementById('bulk-category-select');
                      const newEl = document.getElementById('bulk-category-new');
                      const category = newEl.value.trim() || selectEl.value;
                      if (!category) {
                        showToast('Please select or enter a category', 'warning');
                        return;
                      }
                      handleBulkRecategorize(category);
                    }}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium"
                  >
                    Update Category
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analyzed Items Dialog */}
        {analyzedItems && (
          <AnalyzedItemsDialog
            items={analyzedItems}
            defaultLocation={filter === 'refrigerator' ? 'refrigerator' : filter === 'pantry' ? 'pantry' : 'pantry'}
            existingCategories={allCategories}
            onClose={() => setAnalyzedItems(null)}
            onConfirm={async (confirmedItems) => {
              try {
                for (const item of confirmedItems) {
                  await addPantryItem(item);
                }
                setAnalyzedItems(null);
                showToast(`Added ${confirmedItems.length} item(s) to pantry!`, 'success');
              } catch (error) {
                console.error('Error adding analyzed items:', error);
                showToast('Failed to add items: ' + error.message, 'error');
              }
            }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, itemId: null })}
          onConfirm={confirmDeleteItem}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    </div>
  );
};

export default PantryPage;

